import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BACKEND_URL } from "@/constants/api";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/layouts/PageHeader";
import { PageContainer } from "@/layouts/PageContainer";
import { SchoolAdminDashboard } from "@/components/entities/SchoolAdminDashboard";
import { MemberList } from "@/components/entities/MemberList";
import { JoinSchoolModal } from "@/components/modals/JoinSchoolModal";
import { CreateSchoolModal } from "@/components/modals/CreateSchoolModal";
import { useSchool } from "@/context/SchoolContext";
import {
  Building2, Lock, Unlock, ShieldCheck,
  AlertCircle, Plus, Users, ArrowRight,
} from "lucide-react";

export default function School() {
  const { memberships, loading: ctxLoading, refetch: refetchCtx } = useSchool();
  const schoolId = memberships[0]?.school?._id;

  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [school, setSchool] = useState(null);
  const [members, setMembers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(null);
  const [acceptingTransfer, setAcceptingTransfer] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    if (!schoolId) return;
    try {
      setLoading(true);
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };

      const [schoolRes, membersRes, userRes] = await Promise.all([
        fetch(`${BACKEND_URL}api/schools/${schoolId}`, { headers }),
        fetch(`${BACKEND_URL}api/schools/${schoolId}/members`, { headers }),
        fetch(`${BACKEND_URL}api/user`, { headers }),
      ]);

      if (!schoolRes.ok) {
        const body = await schoolRes.json().catch(() => ({}));
        throw new Error(body.message || "Failed to load school");
      }
      const schoolData = await schoolRes.json();
      setSchool(schoolData);
      if (userRes.ok) setCurrentUser(await userRes.json());

      if (membersRes.ok) {
        setMembers(await membersRes.json());
        if (schoolData.isAdmin && schoolData.sharingMode === "restricted") {
          const srRes = await fetch(`${BACKEND_URL}api/schools/${schoolId}/share-requests`, { headers });
          if (srRes.ok) setPendingCount((await srRes.json()).length);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [schoolId, getToken]);

  useEffect(() => { load(); }, [load]);

  const copyInviteCode = () => {
    if (!school?.inviteCode) return;
    navigator.clipboard.writeText(school.inviteCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const regenerateCode = async () => {
    if (!confirm("Regenerate invite code? The old code will stop working immediately.")) return;
    setRegenerating(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/regenerate-code`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to regenerate code");
      const { inviteCode } = await res.json();
      setSchool((prev) => ({ ...prev, inviteCode }));
      toast.success("Invite code regenerated.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRegenerating(false);
    }
  };

  const removeMember = async (userId, name) => {
    if (!confirm(`Remove ${name} from this school?`)) return;
    setActionInProgress(userId + "remove");
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/members/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to remove member");
      }
      setMembers((prev) => prev.filter((m) => String(m.userId) !== userId));
      toast.success("Member removed.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleLeave = async () => {
    if (!confirm(`Leave "${school?.name}"? You will lose access to shared classes.`)) return;
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/leave`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to leave school");
      }
      toast.success("You have left the school.");
      refetchCtx();
      navigate("/schools");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const acceptTransfer = async () => {
    if (!confirm("Accept the admin role for this school? The current admin will lose their admin access.")) return;
    setAcceptingTransfer(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/admin-transfer/accept`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to accept transfer");
      }
      toast.success("You are now the school admin.");
      refetchCtx();
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAcceptingTransfer(false);
    }
  };

  // ── Empty state ────────────────────────────────────────────────────────────
  if (!ctxLoading && memberships.length === 0) {
    return (
      <div className="px-10 py-9 max-w-[960px] mx-auto max-md:px-4 max-md:pt-5 max-md:pb-6">
        <PageHeader
          title="My School"
          subtitle="Manage your school membership and class sharing settings."
          action={
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="gap-2" onClick={() => setShowJoin(true)}>
                <Users size={14} />
                Join School
              </Button>
              <Button size="sm" className="gap-2" onClick={() => setShowCreate(true)}>
                <Plus size={14} />
                Create School
              </Button>
            </div>
          }
        />

        <div className="flex flex-col items-center justify-center gap-3 py-14 text-center rounded-2xl border border-dashed border-border bg-muted/20 mb-8">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <Building2 size={22} className="text-primary" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">Not part of any school yet</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-xs">
              Create a school to manage teachers and class sharing, or join one using an invite code from your admin.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 max-[560px]:grid-cols-1">
          <button className="group flex items-center gap-4 px-5 py-[1.125rem] bg-card border border-border rounded-xl no-underline text-foreground transition-[border-color,box-shadow,transform] duration-150 hover:border-primary hover:shadow-[0_2px_12px_rgba(0,0,0,0.07)] hover:-translate-y-px" onClick={() => setShowCreate(true)}>
            <div className="w-[42px] h-[42px] rounded-[10px] bg-accent flex items-center justify-center text-primary shrink-0"><Building2 size={22} /></div>
            <div>
              <div className="text-[0.9375rem] font-semibold">Create a School</div>
              <div className="text-[0.8125rem] text-muted-foreground mt-[0.15rem]">Set up a school, invite teachers, and control class sharing</div>
            </div>
            <ArrowRight size={16} className="ml-auto text-muted-foreground shrink-0 transition-transform group-hover:translate-x-[3px] group-hover:text-primary" />
          </button>
          <button className="group flex items-center gap-4 px-5 py-[1.125rem] bg-card border border-border rounded-xl no-underline text-foreground transition-[border-color,box-shadow,transform] duration-150 hover:border-primary hover:shadow-[0_2px_12px_rgba(0,0,0,0.07)] hover:-translate-y-px" onClick={() => setShowJoin(true)}>
            <div className="w-[42px] h-[42px] rounded-[10px] bg-accent flex items-center justify-center text-primary shrink-0"><Users size={22} /></div>
            <div>
              <div className="text-[0.9375rem] font-semibold">Join a School</div>
              <div className="text-[0.8125rem] text-muted-foreground mt-[0.15rem]">Enter an invite code from your school admin to join</div>
            </div>
            <ArrowRight size={16} className="ml-auto text-muted-foreground shrink-0 transition-transform group-hover:translate-x-[3px] group-hover:text-primary" />
          </button>
        </div>

        <JoinSchoolModal open={showJoin} onClose={() => { setShowJoin(false); refetchCtx(); }} />
        <CreateSchoolModal open={showCreate} onClose={() => { setShowCreate(false); refetchCtx(); }} />
      </div>
    );
  }

  // ── Loading / error ────────────────────────────────────────────────────────
  if (ctxLoading || loading) {
    return (
      <PageContainer size="narrow">
        <div className="h-7 w-56 bg-muted animate-pulse rounded mb-2" />
        <div className="h-4 w-32 bg-muted animate-pulse rounded mb-8" />
        <div className="space-y-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-14 bg-muted animate-pulse rounded-xl" />)}
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer size="narrow">
        <div className="flex items-center gap-2 text-destructive text-sm rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <AlertCircle size={15} className="shrink-0" />
          {error}
        </div>
      </PageContainer>
    );
  }

  // ── School detail ──────────────────────────────────────────────────────────
  const isAdmin = school?.isAdmin ?? false;
  const isPendingTransferTarget =
    school?.pendingAdminTransfer?.toUserId &&
    currentUser?._id &&
    String(school.pendingAdminTransfer.toUserId) === String(currentUser._id);

  return (
    <PageContainer size="narrow">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <Building2 size={18} className="text-primary" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-normal text-foreground">{school?.name}</h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              {school?.sharingMode === "restricted"
                ? <><Lock size={10} />Restricted sharing</>
                : <><Unlock size={10} />Free sharing</>}
            </span>
            {isAdmin && (
              <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                <ShieldCheck size={10} />
                You are admin
              </span>
            )}
          </div>
        </div>
      </div>

      {isPendingTransferTarget && (
        <div className="flex items-start justify-between gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3.5 mb-6 flex-wrap gap-y-3">
          <div className="flex items-start gap-2.5">
            <ShieldCheck size={15} className="text-primary mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-foreground">
                You have been invited to become admin of this school
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                The current admin wants to transfer their role to you. Accept to take over
                admin responsibilities.
              </p>
            </div>
          </div>
          <Button
            size="sm"
            className="gap-1.5 h-8 shrink-0"
            disabled={acceptingTransfer}
            onClick={acceptTransfer}
          >
            <ShieldCheck size={13} />
            {acceptingTransfer ? "Accepting..." : "Accept Admin Role"}
          </Button>
        </div>
      )}

      {isAdmin && (
        <SchoolAdminDashboard
          school={school}
          pendingCount={pendingCount}
          copied={copied}
          regenerating={regenerating}
          onCopy={copyInviteCode}
          onRegenerate={regenerateCode}
        />
      )}

      <div className="mb-2">
        <h2 className="text-[0.8125rem] font-semibold tracking-[0.07em] uppercase text-muted-foreground mb-0">
          Members ({members.length})
        </h2>
      </div>

      <MemberList
        members={members}
        isViewerAdmin={isAdmin}
        onRemove={removeMember}
        actionInProgress={actionInProgress}
      />

      {!isAdmin && (
        <div className="mt-8 space-y-4">
          <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/30 px-4 py-3.5">
            <Lock size={15} className="text-muted-foreground mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-foreground">You are not permitted to change school settings</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Contact your school admin to update sharing settings, domain restrictions, or member permissions.
              </p>
            </div>
          </div>
          <Button
            variant="outline" size="sm"
            className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
            onClick={handleLeave}
          >
            Leave School
          </Button>
        </div>
      )}
    </PageContainer>
  );
}
